import { useCallback } from 'react';
import { firstValueFrom } from 'rxjs';
import {
  isFollowing$,
  UserProfileOpenScene,
} from '../../appComponents/commonAPI/CommonAPIProvider';
import type { ProfileTabKey } from '../../flows/profiles/components/ProfileHomeTabs';
import { ProfilePreviewInfo } from '../../flows/profiles/components/WiredProfileAboutContent';
import type { ProfileInitialNavigationTarget } from '../../flows/profiles/ProfileContext';
import { CurrentShow } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow';
import { Profile, ShowPermission } from '../../models/ClientModels';
import { ScreenLinkIdParams } from '../../utils/firebaseDynamicLinkHelpers';
import { firestore } from '../firebase/firebaseAccess';
import { userProfile$ } from '../firebase/observables/userProfile$';
import { snapshotToDataWithId, WithID } from '../firebase/rxfireExtensions';
import { isSeller } from '../models/profiles';
import { useRootNavigation } from '../navigation/PopNavigatorProvider';
import { OrderSourceType } from '../order/orderServiceHelpers';
import { SpecialUrlCode } from '../urlCodeHelpers';
import { usePersistFn } from './usePersistFn';

type Params = ScreenLinkIdParams & {
  title?: string;
  scene?: UserProfileOpenScene;
  source?: OrderSourceType;
  sourceShow?: CurrentShow;
  preview?: ProfilePreviewInfo;
  initialTab?: ProfileTabKey;
  initialNavigateTarget?: ProfileInitialNavigationTarget;
};

export type UserProfileAction =
  | {
      type: 'profile';
      profile: WithID<Profile>;
      params: Params & {
        uid: string;
      };
    }
  | {
      type: 'userProfile';
      profile: WithID<Profile>;
      params: Params & {
        username: string;
      };
    }
  | {
      type: 'sellerProfile';
      profile: WithID<Profile>;
      params: Params & {
        sellerUrlCode: string;
      };
    }
  | {
      type: 'showcase';
      profile: WithID<Profile>;
      params: { urlCode: string };
    };

type Props = {
  profile?: WithID<Profile>;
  /** specify title if the profile is intended to be open in a normal stack sceen */
  title?: string | ((profile: WithID<Profile>) => string);
  isFollowing?: boolean;
  target?: 'profile' | 'auto';
  params?: Record<string, any>;
  hideFloatingCanvas?: boolean;
} & Omit<Params, 'title'>;

export const useUserProfileHandler = (
  props: Props & {
    handle: (action: UserProfileAction) => void;
  },
) => {
  const { profile, ...restProps } = props;
  const handler = useUserProfileHandlerFactory(restProps);
  const onPress = useCallback(async () => {
    await handler(profile);
  }, [handler, profile]);
  return { onPress };
};

export const useOpenUserProfile = (props?: Props) => {
  const navigation = useRootNavigation();
  const openProfile = useUserProfileHandlerFactory({
    target: props?.target ?? 'profile',
    handle: action => {
      navigation.push(action.type as any, action.params);
    },
  });
  return useCallback(
    async (
      params?: Params &
        (
          | {
              uid?: string;
            }
          | { profile?: WithID<Profile> }
        ),
    ) => {
      const {
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        uid: _uid,
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        profile: _profile,
        ...additionalParams
      } = params ?? {};
      if (params != null && 'profile' in params && params.profile) {
        await openProfile(params.profile ?? props?.profile, additionalParams);
      } else if (params != null && 'uid' in params && params.uid) {
        const profile = await firstValueFrom(userProfile$(params.uid));
        await openProfile(profile, additionalParams);
      } else {
        await openProfile(props?.profile, additionalParams);
      }
    },
    [openProfile, props?.profile],
  );
};

export const useUserProfileHandlerFactory = ({
  handle,
  title: titleOrGetter,
  isFollowing,
  target,
  params,
  ...props
}: Omit<Props, 'profile'> & {
  handle: (action: UserProfileAction) => void;
}) => {
  return usePersistFn(
    async (profile: WithID<Profile> | undefined, additionalParams?: Params) => {
      if (!profile) {
        return;
      }

      const title =
        typeof titleOrGetter === 'function'
          ? titleOrGetter(profile)
          : titleOrGetter;

      const handleProfile = (profile: WithID<Profile>) => {
        if (isSeller(profile) && profile.urlCode) {
          handle({
            type: 'sellerProfile',
            profile,
            params: {
              sellerUrlCode: profile.urlCode,
              title,
              ...props,
              ...params,
              ...additionalParams,
            },
          });
        } else if (profile.username) {
          handle({
            type: 'userProfile',
            profile,
            params: {
              username: profile.username,
              title,
              ...props,
              ...params,
              ...additionalParams,
            },
          });
        } else {
          handle({
            type: 'profile',
            profile,
            params: {
              uid,
              title,
              ...props,
              ...params,
              ...additionalParams,
            },
          });
        }
      };

      const uid = profile.id;
      const isSellerProfile = isSeller(profile);

      if (!isSellerProfile || target === 'profile') {
        handleProfile(profile);
        return;
      }

      try {
        const snapshot = await firestore()
          .collection('liveStreams')
          .where('primary.userId', '==', uid)
          .where('primary.status.finished', '==', false)
          .where('primary.status.aborted', '==', false)
          .where('primary.status.initialized', '==', true)
          .limit(1)
          .get();

        const liveStream = snapshot.docs.length
          ? snapshotToDataWithId(snapshot.docs[0])
          : null;

        if (liveStream && liveStream.permission !== ShowPermission.None) {
          if (
            !liveStream.permission ||
            liveStream.permission === ShowPermission.All ||
            (liveStream.permission === ShowPermission.Follower && isFollowing)
          ) {
            handle({
              type: 'showcase',
              profile,
              params: { urlCode: SpecialUrlCode.liveShow(liveStream.id) },
            });
            return;
          }

          // check if the current user is following the target user
          const following = await firstValueFrom(isFollowing$(uid));
          if (following) {
            handle({
              type: 'showcase',
              profile,
              params: {
                urlCode: SpecialUrlCode.liveShow(liveStream.id),
              },
            });
            return;
          }
        }

        // in rare cases, current profile user type is different from the original
        // profile information, so we have to ensure a seller profile to go.
        const currentProfile = await firstValueFrom(userProfile$(profile.id));
        if (!isSeller(currentProfile ?? {})) {
          handleProfile(currentProfile);
          return;
        }
      } catch (e) {
        console.error(e);
      }

      handle({
        type: 'showcase',
        profile,
        params: {
          urlCode: SpecialUrlCode.profile(uid),
        },
      });
    },
  );
};
